import React, { PropsWithChildren } from 'react'
import styled from 'styled-components/native'
import { v4 as uuidv4 } from 'uuid'

import { cookiesInfo } from 'features/cookies/components/cookiesInfo'
import { CookieCategoriesEnum } from 'features/cookies/enums'
import { CookiesChoiceSettings } from 'features/cookies/types'
import { analytics } from 'libs/analytics/provider'
import { Accordion } from 'ui/components/Accordion'
import FilterSwitch from 'ui/components/FilterSwitch'
import { Separator } from 'ui/components/Separator'
import { InfoPlain } from 'ui/svg/icons/InfoPlain'
import { Typo } from 'ui/theme'

export const CookiesAccordion = ({
  cookie,
  settingsCookiesChoice,
  setSettingsCookiesChoice,
  offerId,
}: { cookie: CookieCategoriesEnum } & CookiesChoiceSettings) => {
  const info = cookiesInfo[cookie]
  const isEssential = cookie === CookieCategoriesEnum.essential
  const accordionLabelId = uuidv4()

  const toggleSwitch = () =>
    isEssential
      ? null
      : setSettingsCookiesChoice((prev) => ({
          ...prev,
          [cookie]: !settingsCookiesChoice[cookie],
        }))

  const defaultOpen = cookie === CookieCategoriesEnum.video && !!offerId

  return (
    <React.Fragment>
      <StyledAccordionItem
        title={<Typo.Body>{info.title}</Typo.Body>}
        onOpenOnce={() => analytics.logHasOpenedCookiesAccordion(cookie)}
        labelId={accordionLabelId}
        leftComponent={
          <FilterSwitch
            testID={info.title}
            active={isEssential ? true : settingsCookiesChoice[cookie]}
            disabled={isEssential}
            accessibilityLabelledBy={accordionLabelId}
            toggle={toggleSwitch}
            accessibilityLabel={info.title}
          />
        }
        defaultOpen={defaultOpen}>
        <React.Fragment>
          <Typo.Body>{info.description}</Typo.Body>
          {info.caption ? <InfoCaption marginTop>{info.caption}</InfoCaption> : null}
        </React.Fragment>
      </StyledAccordionItem>
      {info.permanentCaption ? (
        <InfoCaption marginBottom>{info.permanentCaption}</InfoCaption>
      ) : null}
      <Separator.Horizontal />
    </React.Fragment>
  )
}

const InfoCaption: React.FC<PropsWithChildren<{ marginTop?: boolean; marginBottom?: boolean }>> = ({
  children,
  marginTop,
  marginBottom,
}) => (
  <StyledView marginTop={marginTop} marginBottom={marginBottom}>
    <IconContainer>
      <StyledInfo />
    </IconContainer>
    <CaptionNeutralInfo>{children}</CaptionNeutralInfo>
  </StyledView>
)

const StyledView = styled.View<{ marginTop?: boolean; marginBottom?: boolean }>(
  ({ theme, marginTop, marginBottom }) => ({
    marginTop: marginTop ? theme.designSystem.size.spacing.s : 0,
    marginBottom: marginBottom ? theme.designSystem.size.spacing.s : 0,
    flexDirection: 'row',
    gap: theme.designSystem.size.spacing.s,
  })
)

const IconContainer = styled.View(({ theme }) => ({
  width: theme.designSystem.size.icon.s,
  height: theme.designSystem.size.icon.s,
  justifyContent: 'center',
  alignItems: 'center',
}))

const StyledInfo = styled(InfoPlain).attrs(({ theme }) => ({
  size: theme.designSystem.size.icon.s,
  color: theme.designSystem.color.icon.brandPrimary,
}))``

const StyledAccordionItem = styled(Accordion).attrs(({ theme }) => ({
  titleStyle: {
    paddingVertical: theme.designSystem.size.spacing.l,
    paddingHorizontal: 0,
  },
  bodyStyle: {
    paddingBottom: theme.designSystem.size.spacing.l,
    paddingHorizontal: 0,
  },
}))``

const CaptionNeutralInfo = styled(Typo.BodyAccentXs)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
}))
